import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useUpdatePrivacyPreferencesMutation } from 'store/services/settings/settingsApi';
import './EnableTradeOffersModal.css';

interface EnableTradeOffersModalProps {
  isOpen: boolean;
  /** Dismiss without changing the setting. The pending action is abandoned. */
  onClose: () => void;
  /**
   * The action the user was taking, re-invoked after the flag is on. It must
   * carry its own context (counterparty, card, in-progress offer) — this modal
   * never navigates anywhere, so a continuation that only routes to an empty
   * builder would drop the user back at square one, which is the exact failure
   * the enable-in-place flow exists to prevent.
   */
  onContinue: () => void | Promise<void>;
}

/**
 * Enable-in-place dialog for `allowIncomingTradeOffers`.
 *
 * Reached three ways, all of them a PROPOSER_OFFERS_DISABLED rejection: clicking
 * Add to Trade on a profile or search result, proposing from the builder, and
 * LOADING the builder (the candidates fetch throws the same code). The caller
 * supplies the matching continuation, so the same component serves all three.
 *
 * Copy note: the body deliberately does NOT promise to resume anything. The
 * primary button already says "continue", and a body line like "picks your offer
 * up where you left off" is false everywhere except the builder.
 */
const EnableTradeOffersModal: React.FC<EnableTradeOffersModalProps> = ({
  isOpen,
  onClose,
  onContinue,
}) => {
  const [updatePreferences, { isLoading }] = useUpdatePrivacyPreferencesMutation();
  const [error, setError] = useState<string | null>(null);

  const handleEnable = useCallback(async () => {
    setError(null);
    try {
      // Awaited so the mutation's PrivacyPreferences invalidation has landed
      // before the continuation re-reads the flag; otherwise a cached `false`
      // bounces the user straight back into the dialog they just dismissed.
      await updatePreferences({ allowIncomingTradeOffers: true }).unwrap();
    } catch {
      // The flip itself failed: leave the setting alone, say so, and start
      // nothing. The user is still where they were.
      setError('We could not turn trade offers back on. Please try again.');
      return;
    }

    // Past this point the setting IS on, and it stays on even if the
    // continuation fails — turning it on is what the user asked for and it
    // succeeded. Closing first means a continuation that navigates or opens its
    // own dialog is not fighting this one for the screen.
    onClose();
    try {
      await onContinue();
    } catch {
      // Never leave a changed setting with no result and no explanation.
      toast.info('Trade offers are back on, but that action did not go through. Please try again.');
    }
  }, [updatePreferences, onClose, onContinue]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="enable-offers-overlay"
      role="presentation"
      onClick={isLoading ? undefined : onClose}
    >
      <div
        className="enable-offers-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="enable-offers-title"
        aria-describedby="enable-offers-body"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="enable-offers-title" className="enable-offers-title">
          Turn trade offers back on?
        </h2>
        <p id="enable-offers-body" className="enable-offers-body">
          Your trade offers are off. This also stops you from sending offers.
        </p>
        {error && (
          <p className="enable-offers-error" role="alert">
            {error}
          </p>
        )}
        <div className="enable-offers-actions">
          <button
            type="button"
            className="enable-offers-btn enable-offers-btn--secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Keep offers off
          </button>
          <button
            type="button"
            className="enable-offers-btn enable-offers-btn--primary"
            onClick={handleEnable}
            disabled={isLoading}
          >
            {isLoading ? 'Turning on…' : 'Turn on and continue'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EnableTradeOffersModal;
